import type { Localization } from '../../dependencies/localization';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import { getBaseSeoData } from './BaseSeoData';
import type { SeoData } from './types';
import type { BrowsePage } from '@jet-app/music-classical/models';

/**
 * @method getBrowseSeoData
 * @param {string} url Browse canonical URL
 * @param {BrowsePage} page Browse page object
 * @param {string} intentTab Current Browse tab
 * @param {ObjectGraph} objectGraph
 * @returns {SeoData}
 */
export function getBrowseSeoData(
    url: string,
    page: BrowsePage,
    intentTab: string,
    objectGraph: ObjectGraph,
): SeoData {
    const localization: Localization = inject(
        ObjectGraphType.localization,
        objectGraph,
    );
    const baseSeoData: SeoData = getBaseSeoData(url, objectGraph);
    const siteName: string = localization.string(
        'ASE.Web.MusicClassicalClientJS.Name',
    );

    function getPageTitle() {
        switch (intentTab) {
            case 'instruments': {
                const baseLoc = localization.string(
                    'ASE.Web.MusicClassicalClientJS.Meta.Browse.Tab.Instruments',
                );
                return localization.string(
                    'ASE.Web.MusicClassicalClientJS.ContentA.Dash.ContentB',
                    {
                        contentA: baseLoc,
                        contentB: siteName,
                    },
                );
            }

            case 'playlists': {
                const baseLoc = localization.string(
                    'ASE.Web.MusicClassicalClientJS.Meta.Browse.Tab.Playlists',
                );
                return localization.string(
                    'ASE.Web.MusicClassicalClientJS.ContentA.Dash.ContentB',
                    {
                        contentA: baseLoc,
                        contentB: siteName,
                    },
                );
            }

            case 'catalog':
            default: {
                const baseLoc = localization.string(
                    'ASE.Web.MusicClassicalClientJS.Meta.Browse.Tab.Catalog',
                );
                return localization.string(
                    'ASE.Web.MusicClassicalClientJS.ContentA.Dash.ContentB',
                    {
                        contentA: baseLoc,
                        contentB: siteName,
                    },
                );
            }
        }
    }

    function getPageDescription() {
        switch (intentTab) {
            case 'instruments': {
                return localization.string(
                    'ASE.Web.MusicClassicalClientJS.Meta.Browse.Description.Instruments',
                );
            }

            case 'playlists': {
                return localization.string(
                    'ASE.Web.MusicClassicalClientJS.Meta.Browse.Description.Playlists',
                );
            }

            case 'catalog':
            default: {
                return localization.string(
                    'ASE.Web.MusicClassicalClientJS.Meta.Browse.Description.Catalog',
                );
            }
        }
    }

    const pageTitle: string = getPageTitle();
    const description: string = getPageDescription();

    /**
     *
     * Generate SEO data object
     *
     */
    return {
        ...baseSeoData,
        pageTitle,
        description,

        /**
         * Social OG Tags
         */
        socialTitle: pageTitle,
        socialDescription: description,

        /**
         *
         * Browse Apple-spcific Values
         *
         */
        appleContentId: undefined,
        appleTitle: pageTitle,
        appleDescription: description,
    };
}

export function addingBrowseSeoData(
    url: string,
    page: BrowsePage,
    intentTab: string,
    objectGraph: ObjectGraph,
): BrowsePage {
    return {
        ...page,
        seoData: getBrowseSeoData(url, page, intentTab, objectGraph),
    };
}
